import {
  browserTimezone,
  convertToTimezone,
  convertToUtc,
  convertWeekdayToTimezone,
  type Frequency,
  type Recurrence,
  type RecurrenceWithRepeat,
  weekdayNames,
} from "@gigpro/utils/datetime";
import { type InfiniteData, type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { produce } from "immer";
import type { GigDirectOfferReadonly } from "../../generated/model/gigDirectOfferReadonly";
import type { GigShiftSerializerWithGigs } from "../../generated/model/gigShiftSerializerWithGigs";
import type { GigShiftUpdate } from "../../generated/model/gigShiftUpdate";
import type { OrganizationGigCandidate } from "../../generated/model/organizationGigCandidate";
import type { PaginatedGigDirectOfferReadonlyList } from "../../generated/model/paginatedGigDirectOfferReadonlyList";
import type { PaginatedStafferGigViewReadonlySerializerV2List } from "../../generated/model/paginatedStafferGigViewReadonlySerializerV2List";
import type { PatchedGigShiftUpdateRequest } from "../../generated/model/patchedGigShiftUpdateRequest";
import type { PatchedStafferGigApplicationUpdateRequest } from "../../generated/model/patchedStafferGigApplicationUpdateRequest";
import type { PatchedStafferGigUpdateRequest } from "../../generated/model/patchedStafferGigUpdateRequest";
import type { PreviewRecurringGig } from "../../generated/model/previewRecurringGig";
import type { RecurringPatternWithTimezone } from "../../generated/model/recurringPatternWithTimezone";
import type { StafferGigCreate } from "../../generated/model/stafferGigCreate";
import type { StafferGigCreateRequest } from "../../generated/model/stafferGigCreateRequest";
import type { StafferGigsterProfilesAvailabilityListParams } from "../../generated/model/stafferGigsterProfilesAvailabilityListParams";
import type { StafferGigUpdate } from "../../generated/model/stafferGigUpdate";
import type { StafferGigViewReadonlySerializerV2 } from "../../generated/model/stafferGigViewReadonlySerializerV2";
import type { StafferGigViewReadonlySerializerV3 } from "../../generated/model/stafferGigViewReadonlySerializerV3";
import {
  stafferGigApplicationsPartialUpdate,
  stafferGigsCreate,
  stafferGigsPartialUpdate,
  stafferGigsGigShiftsPartialUpdate,
  getStafferGigsterProfilesAvailabilityListQueryOptions,
  getStafferGigsV2CalendarListQueryOptions,
  getStafferGigsV2ListInfiniteQueryOptions,
  getStafferGigsV2RecurringPatternGigsListQueryOptions,
  stafferGigsterProfilesAvailabilityList,
  stafferGigsV2Retrieve,
  getStafferGigsV2ListQueryOptions,
  useStafferGigsDirectOfferListInfinite,
  useStafferGigsRecurringPatternRetrieve,
  useStafferGigsterProfilesAvailabilityList,
  useStafferGigsV2List,
  useStafferOrganizationsGigsList,
  useStafferOrganizationsShiftsList,
} from "../../generated/staffer/staffer";
import { zoneAndSortShifts } from "../events/events";
import { composeSelect, type Tz } from "../types";

const applyTzToGig = <T extends { start_time: Date; end_time: Date; timezone: string }>(gig: T): T =>
  produce(gig, (draft) => {
    draft.start_time = convertToTimezone(draft.start_time, draft.timezone);
    draft.end_time = convertToTimezone(draft.end_time, draft.timezone);
  });

function zoneGigDates<T extends { start_time: Date; end_time: Date; timezone: string }>(gigs: Array<T>) {
  return gigs.map(applyTzToGig);
}

export const stafferGigsV2RetrieveWithTransform = (id: number) =>
  stafferGigsV2Retrieve(id).then((gig) =>
    produce(gig, (draft) => {
      const tz = draft.location.timezone ?? browserTimezone;
      draft.start_time = convertToTimezone(draft.start_time, tz);
      draft.end_time = convertToTimezone(draft.end_time, tz);
      if (draft.projected_push_back_close_time) {
        draft.projected_push_back_close_time = convertToTimezone(draft.projected_push_back_close_time, tz);
      }
    }),
  );

const zoneGigV2 = produce<StafferGigViewReadonlySerializerV2>((draft) => {
  const tz = draft.location.timezone ?? browserTimezone;
  draft.start_time = convertToTimezone(draft.start_time, tz);
  draft.end_time = convertToTimezone(draft.end_time, tz);
});

const zoneGigsV2Page = (data: PaginatedStafferGigViewReadonlySerializerV2List) => ({
  ...data,
  results: data.results.map(zoneGigV2),
});

export function useStafferGigsV2ListWithTransform<TData = PaginatedStafferGigViewReadonlySerializerV2List>(
  ...[params, options]: Parameters<typeof useStafferGigsV2List<TData>>
) {
  return useStafferGigsV2List<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(zoneGigsV2Page, options?.query?.select),
    },
  });
}

export const getStafferGigsV2ListWithTransformQueryOptions = <TData = PaginatedStafferGigViewReadonlySerializerV2List>(
  ...[params, options]: Parameters<typeof getStafferGigsV2ListQueryOptions<TData>>
) =>
  getStafferGigsV2ListQueryOptions<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(zoneGigsV2Page, options?.query?.select),
    },
  });

export function getStafferGigsV2ListInfiniteWithTransformQueryOptions<
  TData = InfiniteData<PaginatedStafferGigViewReadonlySerializerV2List, number | undefined>,
>(...[params, options]: Parameters<typeof getStafferGigsV2ListInfiniteQueryOptions<TData>>) {
  return getStafferGigsV2ListInfiniteQueryOptions<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(
        (data: InfiniteData<PaginatedStafferGigViewReadonlySerializerV2List, number | undefined>) => ({
          ...data,
          pages: data.pages.map((page) => ({ ...page, results: page.results.map(zoneGigV2) })),
        }),
        options?.query?.select,
      ),
    },
  });
}

const zoneDirectOfferGig = produce<GigDirectOfferReadonly>((draft) => {
  const tz = draft.location.timezone ?? browserTimezone;
  draft.start_time = convertToTimezone(draft.start_time, tz);
  draft.end_time = convertToTimezone(draft.end_time, tz);
});

export function useStafferGigsDirectOfferListInfiniteWithTransform<TData = PaginatedGigDirectOfferReadonlyList>(
  ...[params, options]: Parameters<typeof useStafferGigsDirectOfferListInfinite<TData>>
) {
  return useStafferGigsDirectOfferListInfinite<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(
        (data: InfiniteData<PaginatedGigDirectOfferReadonlyList, number | undefined>) => ({
          ...data,
          pages: data.pages.map((page) => ({ ...page, results: page.results.map(zoneDirectOfferGig) })),
        }),
        options?.query?.select,
      ),
    },
  });
}

export function getStafferGigsV2CalendarListWithTransformQueryOptions<
  TData = Array<StafferGigViewReadonlySerializerV3>,
>(...[params, options]: Parameters<typeof getStafferGigsV2CalendarListQueryOptions<TData>>) {
  return getStafferGigsV2CalendarListQueryOptions<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(
        (data: Array<StafferGigViewReadonlySerializerV3>) => zoneGigDates(data),
        options?.query?.select,
      ),
    },
  });
}

export function getStafferGigsV2RecurringPatternGigsListWithTransformQueryOptions<TData = Array<PreviewRecurringGig>>(
  ...[params, options]: Parameters<typeof getStafferGigsV2RecurringPatternGigsListQueryOptions<TData>>
) {
  return getStafferGigsV2RecurringPatternGigsListQueryOptions<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect((data: Array<PreviewRecurringGig>) => zoneGigDates(data), options?.query?.select),
    },
  });
}

export function useStafferOrganizationsShiftsListWithTransform<TData = Array<GigShiftSerializerWithGigs>>(
  ...[id, params, options]: Parameters<typeof useStafferOrganizationsShiftsList<TData>>
) {
  return useStafferOrganizationsShiftsList<TData>(id, params, {
    ...options,
    query: { ...options?.query, select: composeSelect(zoneAndSortShifts, options?.query?.select) },
  });
}

interface GigShiftUpdateRequestWithTransform extends PatchedGigShiftUpdateRequest, Tz {}
type GigShiftUpdateWithTransformVariables = { id: number; data: GigShiftUpdateRequestWithTransform };
interface StafferGigCreateWithTransformVariables extends Omit<StafferGigCreateRequest, "recurring_pattern">, Tz {
  recurrence?: RecurrenceWithRepeat;
  save?: boolean;
  /** Sent as request headers (e.g. `Idempotency-Key`), not in the body. */
  headers?: Record<string, string>;
}

const applyTzToGigWrite = <T extends Tz & { start_time?: Date | null; end_time?: Date | null }>(request: T): T =>
  produce(request, (draft) => {
    if (draft.timezone) {
      if (draft.start_time) draft.start_time = convertToUtc(draft.start_time, draft.timezone);
      if (draft.end_time) draft.end_time = convertToUtc(draft.end_time, draft.timezone);
    }
    delete draft.timezone;
  });

export function useStafferGigsGigShiftsPartialUpdateWithTransform(options?: {
  mutation?: Omit<UseMutationOptions<GigShiftUpdate, unknown, GigShiftUpdateWithTransformVariables>, "mutationFn">;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: ({ id, data }: GigShiftUpdateWithTransformVariables) =>
      stafferGigsGigShiftsPartialUpdate(id, applyTzToGigWrite(data)),
  });
}

const toRecurringPattern = (
  recurrence: RecurrenceWithRepeat | undefined,
  start_time: Date | undefined,
  timezone: string,
) => (recurrence?.repeat === "repeat" && start_time ? transformRecurrenceToApi(recurrence, timezone) : undefined);

export const stafferGigsCreateWithTransform = ({
  recurrence,
  save,
  headers,
  ...variables
}: StafferGigCreateWithTransformVariables) =>
  stafferGigsCreate(
    applyTzToGigWrite({
      ...variables,
      recurring_pattern: toRecurringPattern(recurrence, variables.start_time, variables.timezone ?? browserTimezone),
    }),
    save === undefined ? undefined : { save },
    headers ? { headers } : undefined,
  );

export function useStafferGigsCreateWithTransform(options?: {
  mutation?: Omit<
    UseMutationOptions<void | StafferGigCreate, unknown, StafferGigCreateWithTransformVariables>,
    "mutationFn"
  >;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: stafferGigsCreateWithTransform,
  });
}

// Validate accepts a partial payload (form steps validate subsets); the generated type models only the full
// create body, hence the cast at the call boundary.
interface StafferGigValidateWithTransformVariables
  extends Partial<Omit<StafferGigCreateRequest, "recurring_pattern">>, Tz {
  recurrence?: RecurrenceWithRepeat;
}

export const stafferGigsValidateWithTransform = ({
  recurrence,
  ...variables
}: StafferGigValidateWithTransformVariables) =>
  stafferGigsCreate(
    applyTzToGigWrite({
      ...variables,
      recurring_pattern: toRecurringPattern(recurrence, variables.start_time, variables.timezone ?? browserTimezone),
    }) as StafferGigCreateRequest,
    { save: false },
  );

export function useStafferGigsValidateWithTransform(options?: {
  mutation?: Omit<
    UseMutationOptions<void | StafferGigCreate, unknown, StafferGigValidateWithTransformVariables>,
    "mutationFn"
  >;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: stafferGigsValidateWithTransform,
  });
}

// `type` is a client-side routing key (which section editor fired), not a wire field — toGigUpdateRequest strips it.
type StafferGigEditInput =
  | ({ type: "wage_amount" } & Pick<PatchedStafferGigUpdateRequest, "wage_amount">)
  | ({ type: "manager_on_duty" } & Pick<
      PatchedStafferGigUpdateRequest,
      "contact_name" | "contact_phone" | "onsite_manager_id"
    >)
  | ({ type: "confirmed_pro" } & Pick<PatchedStafferGigUpdateRequest, "allowed_confirmed_pros">)
  | ({ type: "boosted_amount" } & Pick<PatchedStafferGigUpdateRequest, "boosted_gig_bonus">)
  | {
      type: "shift_times";
      start_time: Date;
      end_time: Date;
      unpaid_break_minutes?: number;
      timezone: string;
      recurrence?: Pick<Recurrence, "weekdays" | "occurrence">;
    }
  | { type: "recurrence_until"; timezone: string; until: Date }
  | ({ type: "private_notes" } & Pick<PatchedStafferGigUpdateRequest, "special_inst">)
  | ({ type: "description" } & Pick<PatchedStafferGigUpdateRequest, "description">)
  | ({ type: "requirements" } & Pick<PatchedStafferGigUpdateRequest, "selected_requirement_ids">)
  | ({ type: "uniform" } & Pick<PatchedStafferGigUpdateRequest, "uniform">)
  | ({ type: "attachments" } & Pick<PatchedStafferGigUpdateRequest, "attachments">)
  | ({ type: "parking" } & Pick<
      PatchedStafferGigUpdateRequest,
      "parking_expense_type" | "parking_instructions" | "attachments"
    >)
  | { type: "promoteToMarketplace" }
  | ({ type: "unpaid_break_time" } & Required<Pick<PatchedStafferGigUpdateRequest, "unpaid_break_minutes">>)
  | ({ type: "po_number" } & Required<Pick<PatchedStafferGigUpdateRequest, "po_number">>)
  | ({ type: "tip_policy" } & Required<Pick<PatchedStafferGigUpdateRequest, "tip_policy">>)
  | ({ type: "overfill" } & Pick<
      PatchedStafferGigUpdateRequest,
      "allowed_confirmed_pros" | "overfill_type" | "manual_overfill_pros_count"
    >);

export type StafferGigEditVariables = StafferGigEditInput & { include_recurring_gigs?: boolean };

const toGigUpdateRequest = (variables: StafferGigEditVariables): PatchedStafferGigUpdateRequest => {
  const { include_recurring_gigs } = variables;
  switch (variables.type) {
    case "shift_times": {
      const { start_time, end_time, unpaid_break_minutes, timezone, recurrence } = variables;
      return {
        include_recurring_gigs,
        start_time: convertToUtc(start_time, timezone),
        end_time: convertToUtc(end_time, timezone),
        unpaid_break_minutes,
        recurring_pattern: recurrence
          ? {
              byweekdays: recurrence.weekdays.map((weekday) => ({
                weekday: weekdayNames[weekday],
                occurrence: recurrence.occurrence,
              })),
              weekdays_are_local: true,
            }
          : undefined,
      };
    }
    case "recurrence_until":
      return {
        include_recurring_gigs,
        recurring_pattern: { until: convertToUtc(variables.until, variables.timezone) },
      };
    case "promoteToMarketplace":
      return { include_recurring_gigs, type: "MARKETPLACE" };
    default: {
      const { type: _type, ...payload } = variables;
      return payload;
    }
  }
};

export const stafferGigsPartialUpdateWithTransform = (id: number, variables: StafferGigEditVariables) =>
  stafferGigsPartialUpdate(id, toGigUpdateRequest(variables));

export function useStafferGigsPartialUpdateWithTransform(
  id: number,
  options?: { mutation?: Omit<UseMutationOptions<StafferGigUpdate, unknown, StafferGigEditVariables>, "mutationFn"> },
) {
  return useMutation({
    ...options?.mutation,
    mutationFn: (variables: StafferGigEditVariables) => stafferGigsPartialUpdateWithTransform(id, variables),
  });
}

/** The venue's own weekdays, marked so the API reads them as such; `until`'s venue date is the last day. */
export function transformRecurrenceToApi(recurrence: Recurrence, timezone: string) {
  return {
    freq: recurrence.frequency,
    interval: recurrence.interval,
    until: recurrence.until ? convertToUtc(recurrence.until, timezone) : undefined,
    byweekdays: recurrence.weekdays.map((day) => ({
      weekday: weekdayNames[day],
      occurrence: recurrence.occurrence,
    })),
    weekdays_are_local: true,
  };
}

export function toRecurrenceWithRepeat(
  { freq, interval, byweekdays, until, timezone, weekdays_are_local }: RecurringPatternWithTimezone,
  baseDate: Date,
): RecurrenceWithRepeat {
  return {
    frequency: freq as Frequency,
    interval: interval ?? 1,
    occurrence: byweekdays[0]?.occurrence ?? 0,
    // An unmarked series stores UTC days; it stays unmarked until a write sends its days.
    weekdays: byweekdays.map(({ weekday }) =>
      weekdays_are_local
        ? weekdayNames.indexOf(weekday)
        : convertWeekdayToTimezone(weekdayNames.indexOf(weekday), baseDate, timezone),
    ),
    until: until ? convertToTimezone(until, timezone) : undefined,
    repeat: "repeat",
  };
}

type RecurringPatternRetrieveGenOptions<TData> = NonNullable<
  Parameters<typeof useStafferGigsRecurringPatternRetrieve<TData>>[1]
>;
type RecurringPatternRetrieveOptions<TData> = Omit<RecurringPatternRetrieveGenOptions<TData>, "query"> & {
  query?: Omit<NonNullable<RecurringPatternRetrieveGenOptions<TData>["query"]>, "select"> & {
    select?: (data: RecurrenceWithRepeat) => TData;
  };
};

export function useStafferGigsRecurringPatternRetrieveWithTransform<TData = RecurrenceWithRepeat>(
  id: number,
  baseDate: Date,
  options?: RecurringPatternRetrieveOptions<TData>,
) {
  return useStafferGigsRecurringPatternRetrieve<TData>(id, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(
        (data: RecurringPatternWithTimezone) => toRecurrenceWithRepeat(data, baseDate),
        options?.query?.select,
      ),
    },
  });
}

export interface StafferGigsterProfilesAvailabilityListWithTransformParams
  extends Omit<StafferGigsterProfilesAvailabilityListParams, "profile_ids" | "user_ids">, Tz {
  profile_ids?: Array<number>;
  user_ids?: Array<number>;
}

const toAvailabilityParams = ({
  timezone,
  profile_ids,
  user_ids,
  shift_start_time,
  shift_end_time,
  ...params
}: StafferGigsterProfilesAvailabilityListWithTransformParams): StafferGigsterProfilesAvailabilityListParams => ({
  ...params,
  profile_ids: profile_ids?.join(","),
  user_ids: user_ids?.join(","),
  shift_start_time: timezone ? convertToUtc(shift_start_time, timezone) : shift_start_time,
  shift_end_time: timezone ? convertToUtc(shift_end_time, timezone) : shift_end_time,
});

export function useStafferGigsterProfilesAvailabilityListWithTransform<
  TData = Awaited<ReturnType<typeof stafferGigsterProfilesAvailabilityList>>,
  TError = unknown,
>(
  params: StafferGigsterProfilesAvailabilityListWithTransformParams,
  options?: Parameters<typeof useStafferGigsterProfilesAvailabilityList<TData, TError>>[1],
) {
  return useStafferGigsterProfilesAvailabilityList<TData, TError>(toAvailabilityParams(params), options);
}

export const getStafferGigsterProfilesAvailabilityListQueryOptionsWithTransform = <
  TData = Awaited<ReturnType<typeof stafferGigsterProfilesAvailabilityList>>,
  TError = unknown,
>(
  params: StafferGigsterProfilesAvailabilityListWithTransformParams,
  options?: Parameters<typeof getStafferGigsterProfilesAvailabilityListQueryOptions<TData, TError>>[1],
) => getStafferGigsterProfilesAvailabilityListQueryOptions<TData, TError>(toAvailabilityParams(params), options);

const zoneGigCandidate = produce<OrganizationGigCandidate>((draft) => {
  const tz = draft.address.timezone ?? browserTimezone;
  draft.start_time = convertToTimezone(draft.start_time, tz);
  draft.end_time = convertToTimezone(draft.end_time, tz);
});

export function useStafferOrganizationsGigsListWithTransform<TData = Array<OrganizationGigCandidate>>(
  ...[id, params, options]: Parameters<typeof useStafferOrganizationsGigsList<TData>>
) {
  return useStafferOrganizationsGigsList<TData>(id, params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(
        (data: Array<OrganizationGigCandidate>) => data.map(zoneGigCandidate),
        options?.query?.select,
      ),
    },
  });
}

interface StafferGigApplicationPartialUpdateWithTransformVariables
  extends PatchedStafferGigApplicationUpdateRequest, Tz {
  id: number;
}

/** Reinterpret the nested `working_hours` times as UTC via the `timezone` mixin, then drop the mixin so only
 *  wire fields ship. */
const applyTzToApplicationWrite = (
  request: Omit<StafferGigApplicationPartialUpdateWithTransformVariables, "id">,
): PatchedStafferGigApplicationUpdateRequest =>
  produce(request, (draft) => {
    const { timezone, working_hours } = draft;
    if (timezone && working_hours) {
      if (working_hours.start_time) working_hours.start_time = convertToUtc(working_hours.start_time, timezone);
      if (working_hours.end_time) working_hours.end_time = convertToUtc(working_hours.end_time, timezone);
    }
    delete draft.timezone;
  });

export const stafferGigApplicationsPartialUpdateWithTransform = ({
  id,
  ...data
}: StafferGigApplicationPartialUpdateWithTransformVariables) =>
  stafferGigApplicationsPartialUpdate(id, applyTzToApplicationWrite(data));
